"use client"

import type { VariantProps } from "class-variance-authority"
import type {
	ComponentPropsWithoutRef,
	ComponentType,
	FC,
	ReactNode,
	SVGProps,
} from "react"
import { forwardRef, memo, useMemo } from "react"
import { cva } from "class-variance-authority"
import {
	IconCircleWarningOutline18 as AlertCircleIcon,
	IconCircleCheckOutline18 as CheckCircleIcon,
	IconCircleInfoOutline18 as InfoIcon,
	IconTriangleWarningOutline18 as TriangleAlertIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import { Loader } from "./loader"

export const alertVariants = cva(
	"flex gap-4 rounded-lg p-4 text-sm ring-1 ring-inset",
	{
		variants: {
			variant: {
				default:
					"bg-gray-50 text-gray-700 ring-gray-600/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20",
				info: "bg-blue-50 text-blue-700 ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30",
				success:
					"bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20",
				error:
					"bg-red-50 text-red-700 ring-red-600/10 dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/20",
				warning:
					"bg-yellow-50 text-yellow-700 ring-yellow-600/10 dark:bg-yellow-400/10 dark:text-yellow-400 dark:ring-yellow-400/20",
			},
			size: {
				default: "",
			},
		},
		defaultVariants: {
			variant: "info",
			size: "default",
		},
	},
)

export interface AlertProps
	extends ComponentPropsWithoutRef<"div">, VariantProps<typeof alertVariants> {
	header?: ReactNode
	loading?: boolean
	icon?: ComponentType<{ className?: string }>
}

const AlertIcon: Record<
	NonNullable<VariantProps<typeof alertVariants>["variant"]>,
	LucideIcon
> = {
	default: InfoIcon,
	info: InfoIcon,
	success: CheckCircleIcon,
	error: AlertCircleIcon,
	warning: TriangleAlertIcon,
}

export const Alert = memo(
	forwardRef<HTMLDivElement, AlertProps>(
		(
			{
				className,
				variant,
				size,
				children,
				loading = false,
				header,
				icon,
				...props
			},
			ref,
		) => {
			const Icon = useMemo(() => {
				return icon ?? AlertIcon[variant ?? "info"]
			}, [icon, variant])
			return (
				<div
					className={cn(alertVariants({ variant, size, className }))}
					ref={ref}
					{...props}
				>
					{loading ? (
						<Loader size={5} className="mt-2" />
					) : (
						<Icon className="mt-0.5 h-4 w-4 flex-none" />
					)}
					<div>
						{!!header && <div className="mb-1 font-semibold">{header}</div>}
						{children && <div>{children}</div>}
					</div>
				</div>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
